
import {DigitizationTools} from "../ToolRegistry.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {AngleAuxiliaryVisual} from "../MeasureAuxiliaryVisuals.js";
import {createFeature} from "../Feature.js";
import {captureThreePointsWithPreview} from "./shapeCaptureHelpers.js";
import {angleAtVertex, angleBisector, distanceBetween} from "../geometry/measurementGeometry.js";
import {formatLength} from "./distanceTool.js";

import {T, t} from "../i18n/index.js";
// Mål vinkel: kopieret fra det gamle native "Angle"-måleværktøj og genskrevet
// til Digitaliserings eget Målinger-format (samme kontrakt som Mål højde -
// se heightTool.js): fast antal klik (3), derefter er målingen en rigtig,
// redigerbar 3-punkts LineString. Midterpunktet (punkt 2) er vinklens spids;
// ingen vertex-koordinater, længde-label på de to ben, en vinkel-bue med
// label (AngleAuxiliaryVisual) og én orange resultat-label. Vinklen er den
// rigtige 3D-vinkel mellem benene (som det gamle værktøj), 1 decimal.
export const AngleMeasureTool = {
	id: "digitize_measure_angle",
	name: T("Mål vinkel"),
	category: "measurement",
	kind: "measurement",
	toolbarGroup: "measurement",
	toolbarOrder: 60,
	icon: "measure_angle.svg",
	resultGeometryType: "LineString",
	fixedVertexCount: 3,
	tooltip: T("Mål vinklen mellem to ben i et midterpunkt (vinklens spids), med benenes længder."),
	instructions: T("Venstreklik for at vælge det første punkt, derefter vinklens spids, og til sidst det tredje punkt."),

	customCapture(dm, tool){
		runAngleCapture(dm, tool);
	},
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the finished measurement keeps its labels and angle arc.
	decorateLoaded(visual){ applyAngleRepresentation(visual); },
};

DigitizationTools.register(AngleMeasureTool);

const STEP_INSTRUCTIONS = [
	null,
	T("Venstreklik for at vælge det første punkt (enden af det ene ben)."),
	T("Venstreklik for at vælge vinklens spids (midterpunktet)."),
	T("Venstreklik for at vælge det tredje punkt (enden af det andet ben)."),
];

function runAngleCapture(dm, tool){
	captureThreePointsWithPreview(dm, tool, (n) => (STEP_INSTRUCTIONS[n] ? t(STEP_INSTRUCTIONS[n]) : null)).then((points) => {
		if(!points) return; // ESC/skift af værktøj - en ufærdig vinkel har intet gyldigt at beholde
		finalizeAngle(dm, tool, points);

		// bliv aktiv, som Mål højde: mål videre uden at klikke på ikonet igen
		if(dm.activeTool === tool) dm.startInsertion(tool);
	});
}

function summaryLines(raw, visual){
	if(raw.length < 3) return null;
	const angle = angleAtVertex(raw[1], raw[0], raw[2]);
	if(angle === null) return null;
	return [
		visual.name,
		t("Vinkel: {0}°", angle.toFixed(1)),
		`Ben 1: ${formatLength(distanceBetween(raw[1], raw[0]))}`,
		`Ben 2: ${formatLength(distanceBetween(raw[1], raw[2]))}`,
	];
}

// Længere ude ad halveringslinjen end vinkel-buens egen label (som ligger
// ved 0,425 x det korteste ben), så de to ikke overlapper. Ved en lige vinkel
// (ingen halveringslinje) bruges de tre punkters tyngdepunkt.
function summaryAnchor(raw){
	const bis = angleBisector(raw[1], raw[0], raw[2]);
	if(!bis){
		return {
			x: (raw[0][0] + raw[1][0] + raw[2][0]) / 3,
			y: (raw[0][1] + raw[1][1] + raw[2][1]) / 3,
			z: (raw[0][2] + raw[1][2] + raw[2][2]) / 3,
		};
	}
	const reach = 0.8 * Math.min(distanceBetween(raw[1], raw[0]), distanceBetween(raw[1], raw[2]));
	return {x: raw[1][0] + bis[0] * reach, y: raw[1][1] + bis[1] * reach, z: raw[1][2] + bis[2] * reach};
}

// The finished angle measurement: hidden vertex labels, a length label per leg, the result label and the angle arc, kept in step with the
// geometry. Shared by the capture flow and the restore hook (decorateLoaded) so both give the same result from the same 3 points.
function applyAngleRepresentation(visual){
	visual.setVertexLabelsSuppressed(true);
	visual.enableSegmentLabels((a, b) => formatLength(a.distanceTo(b)));
	visual.enableSummaryLabel(summaryLines, summaryAnchor);

	const auxiliary = new AngleAuxiliaryVisual();
	visual.add(auxiliary);
	visual.userData.auxiliaryVisual = auxiliary;
	const refresh = () => {
		const [a, v, b] = visual.points;
		auxiliary.updateFromPoints(a && a.position, v && v.position, b && b.position);
	};
	refresh();
	visual.addEventListener("geometry_changed", refresh);
}

function finalizeAngle(dm, tool, points){
	const visual = new FeatureVisual("LineString");
	for(const p of points) visual.appendAtEnd(p);
	applyAngleRepresentation(visual);

	const feature = createFeature({
		geometryType: "LineString",
		coordinates: visual.toCoordinates(),
		category: tool.category,
		tool: tool.id,
		featureType: tool.featureType || tool.name,
		name: `${tool.name} ${dm.featureManager.features.length + 1}`,
	});

	visual.setInfo(feature.properties.name, feature.properties.description);
	dm.scene.add(visual);
	dm.registerNewFeature(feature, visual);
}
